import type { User } from '@supabase/supabase-js';
import { emailToUsername, normalizeEmail } from './emailUtils';
import type { UnifiedPortalUser } from './portalUnifiedUsers';
import { unifiedDisplayNameForEmail } from './portalUnifiedUsers';
import type { ApplicationSubmission } from '../types/applicationForm';
import type { CrmUser } from '../types/crm';

/** Internal submissions store the Admin Hub unified display name when available. */
export function resolveInternalSubmitterLabel(
  profile: CrmUser | null,
  authUser: Pick<User, 'email'> | null,
  unifiedUsers?: UnifiedPortalUser[],
): string | undefined {
  const email = profile?.email?.trim() || authUser?.email?.trim();
  if (email && unifiedUsers?.length) {
    const fromUnified = unifiedDisplayNameForEmail(unifiedUsers, email);
    if (fromUnified) return fromUnified;
  }

  const displayName = profile?.display_name?.trim();
  const username = profile?.username?.trim();
  if (displayName) return displayName;
  if (username) return username;
  if (email) return emailToUsername(email);
  return undefined;
}

function resolveSubmitterDisplayName(
  submission: ApplicationSubmission,
  unifiedUsers?: UnifiedPortalUser[],
): string {
  const stored = submission.submitted_by?.trim() || '';
  const email = submission.submitter_email?.trim() || '';

  if (email && unifiedUsers?.length) {
    const fromUnified = unifiedDisplayNameForEmail(unifiedUsers, email);
    if (fromUnified) return fromUnified;
  }

  return stored;
}

export function formatSubmittedBy(
  submission: ApplicationSubmission,
  unifiedUsers?: UnifiedPortalUser[],
): string {
  if (submission.source === 'public') {
    const label = submission.submitted_by?.trim();
    return label === 'customer_portal' ? 'Customer portal' : label || 'Customer portal';
  }

  const email = submission.submitter_email?.trim() || '';
  const name = resolveSubmitterDisplayName(submission, unifiedUsers);

  if (name && email && normalizeEmail(name) !== normalizeEmail(email)) {
    return `${name} · ${email}`;
  }
  return name || email || '—';
}
